// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

// TMI - content type and query definitions
import type { SimplePageContent } from "@tmi/types/entities";
import { cache, CacheClosedError, simplePages } from "@/services/cache";

import { refreshSimplePagesFromServer } from "@/services/data/sync/simple-page-sync-coordinator";
import { NotFound } from "@tmi/types/api/responses";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allSimplePages.value.length > 0 );
const loading = ref< boolean >( false );

const allSimplePagesBySlugMap = new Map< string, SimplePageContent >();
const defaultSimplePages: SimplePageContent[] = [];
const allSimplePages = ref< SimplePageContent[] >( defaultSimplePages );

export const useSimplePagesStore = defineStore( 'simple-pages', () => {
    function reset(): void {
        allSimplePages.value.splice( 0 );
        allSimplePagesBySlugMap.clear();

        loading.value = false;
    }

    async function initializeAsync(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Simple-Pages ].initialize() exiting - already processing...' );
            return;
        }

        try {
            logger.debug( `[ Simple-Pages ] Checking the local database for any simple page content...` );

            loading.value = true;

            const t0: number = performance.now();

            // Wait for the database to open before the first read.
            await cache.ready();

            const uniquePages: Map< string, SimplePageContent > = new Map< string, SimplePageContent >();
            if ( !await simplePages.isEmpty() ) {
                // Clear the contents of the searchable collections
                allSimplePages.value.splice( 0 );

                for ( const page of await simplePages.all() ) {
                    uniquePages.set( page.id, page );
                }

                allSimplePages.value = [ ...( Array.from( uniquePages.values() ) ) ];

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allSimplePages.value.length } simple-pages in ${ t1 - t0 } ms` );
            } else {
                await fetchFromServer();
            }
        } catch ( err: any ) {
            if ( err instanceof CacheClosedError ) { return; }   // a newer build upgraded the cache; this tab is reloading
            logger.error( JSON.stringify( err.message ) );
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    // WHY: Concurrent callers must await the SAME fetch promise. The previous
    // `if ( loading.value ) return;` early-return let the second caller skip
    // the work and observe an empty map → the original double-fetch race in
    // SimpleContentfulPage threw NotFound and rendered blank content tabs.
    let inFlightFetch: Promise< void > | null = null;

    async function fetchFromServer() {
        if ( inFlightFetch ) {
            await inFlightFetch;
            return;
        }

        inFlightFetch = ( async () => {
            try {
                loading.value = true;

                const receivedSimplePages: SimplePageContent[] = await refreshSimplePagesFromServer();
                for ( const sp of receivedSimplePages ) {
                    allSimplePagesBySlugMap.set( sp.slug, sp );
                }

                allSimplePages.value = [ ...( Array.from( allSimplePagesBySlugMap.values() ) ) ];
            } catch ( err: any ) {
                logger.error( JSON.stringify( err.message ) );
                reset();
            } finally {
                loading.value = false;
            }
        } )();

        try {
            await inFlightFetch;
        } finally {
            inFlightFetch = null;
        }
    }

    async function fetchSimplePageBySlugIfMissing( slug: string ): Promise< SimplePageContent | undefined > {
        // Cache hit — return immediately.
        let requestedSimplePage: SimplePageContent | undefined = allSimplePagesBySlugMap.get( slug );
        if ( requestedSimplePage ) {
            return requestedSimplePage;
        }

        // WHY: do NOT wipe the cache before re-fetching. The previous version
        // cleared `allSimplePagesBySlugMap` here, which (a) discarded valid
        // pages other tabs/components depend on and (b) created the race
        // window that broke TSP's Model Portfolio tab on first navigation.
        // `fetchFromServer` is now safe to call concurrently — multiple callers
        // share one in-flight promise.
        await fetchFromServer();

        requestedSimplePage = allSimplePagesBySlugMap.get( slug );
        if ( !requestedSimplePage ) {
            throw new NotFound();
        }

        return requestedSimplePage;
    }

    return {
        // ACTIONS
        fetchSimplePageBySlugIfMissing,
        initializeAsync,
        reset,

        // GETTERS
        allSimplePages
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSimplePagesStore, import.meta.hot ) );
}
